import React from "react";

const selectStyle = {
  padding: "4px 6px", border: "1px solid #E0E0E0", background: "white",
  fontSize: "12px", fontFamily: "Oswald", letterSpacing: "0.04em", maxWidth: 140,
};

export function DemographicCell({ respondent, demographicKeys, demographicConfig, editable, saving, onChange }) {
  if (demographicKeys.length === 0) return <span className="text-gray-500">—</span>;
  if (!editable) {
    return demographicKeys.map((k) => `${k}: ${respondent.demographics?.[k] || "—"}`).join(" · ");
  }
  return (
    <div className="flex flex-wrap gap-2" data-testid={`demo-edit-${respondent.id}`}>
      {demographicKeys.map((k) => (
        <label key={k} className="flex items-center gap-1 text-xs text-gray-500">
          <span>{k}</span>
          <select
            value={respondent.demographics?.[k] || ""}
            disabled={saving}
            onChange={(e) => onChange(respondent, k, e.target.value || null)}
            data-testid={`demo-select-${respondent.id}-${k}`}
            aria-label={`${k} for ${respondent.email || "respondent"}`}
            style={{ ...selectStyle, opacity: saving ? 0.5 : 1 }}
          >
            <option value="">—</option>
            {(demographicConfig[k] || []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
      ))}
    </div>
  );
}

export function BulkAssignBar({ count, demographicConfig, bulkKey, bulkValue, onKey, onValue, onApply, onClear, busy }) {
  const keys = Object.keys(demographicConfig || {});
  const values = demographicConfig?.[bulkKey] || [];
  return (
    <div
      className="flex items-center gap-3 flex-wrap px-4 py-3"
      style={{ background: "#1A1A1A", color: "#FBFAF7" }}
      data-testid="bulk-assign-bar"
    >
      <span style={{ fontFamily: "Oswald", letterSpacing: "0.15em", textTransform: "uppercase", fontSize: "11px" }} data-testid="bulk-selected-count">
        {count} selected
      </span>
      <span className="text-xs" style={{ color: "#BDBDBD" }}>Assign</span>
      <select value={bulkKey} onChange={(e) => onKey(e.target.value)} data-testid="bulk-key-select" aria-label="Dimension" style={{ ...selectStyle, color: "#1A1A1A" }}>
        {keys.map((k) => <option key={k} value={k}>{k}</option>)}
      </select>
      <span className="text-xs" style={{ color: "#BDBDBD" }}>to</span>
      <select value={bulkValue} onChange={(e) => onValue(e.target.value)} data-testid="bulk-value-select" aria-label="Value" style={{ ...selectStyle, color: "#1A1A1A" }}>
        <option value="">Select…</option>
        {values.map((v) => <option key={v} value={v}>{v}</option>)}
        <option value="__clear__">(clear)</option>
      </select>
      <button
        onClick={onApply}
        disabled={busy || !bulkValue}
        data-testid="bulk-assign-apply"
        style={{
          fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase", fontSize: "11px",
          background: !bulkValue || busy ? "#555555" : "#AD393E", color: "#FBFAF7", border: 0,
          padding: "8px 14px", cursor: !bulkValue || busy ? "not-allowed" : "pointer",
          transition: "background-color 150ms",
        }}
      >
        {busy ? "Assigning…" : "Apply"}
      </button>
      <button onClick={onClear} className="text-xs underline" style={{ color: "#BDBDBD" }} data-testid="bulk-clear-selection">
        Clear selection
      </button>
    </div>
  );
}

export function SubgroupSummary({ rows, demographicConfig, threshold = 5 }) {
  const keys = Object.keys(demographicConfig || {});
  if (keys.length === 0 || rows.length === 0) return null;
  const live = rows.filter((r) => !r.anonymized_at);
  return (
    <div className="panel" data-testid="subgroup-summary">
      <div className="eyebrow mb-3">Subgroups</div>
      <div className="space-y-3">
        {keys.map((k) => (
          <div key={k} className="flex items-baseline gap-3 flex-wrap">
            <span className="text-xs text-gray-500 w-20 shrink-0" style={{ fontFamily: "Oswald", letterSpacing: "0.15em", textTransform: "uppercase" }}>{k}</span>
            {[...(demographicConfig[k] || []), null].map((v) => {
              const members = live.filter((r) => (r.demographics?.[k] || null) === v);
              if (v === null && members.length === 0) return null;
              const done = members.filter((r) => r.completed_at).length;
              const small = done > 0 && done < threshold;
              return (
                <span
                  key={v ?? "_unassigned"}
                  className="text-xs px-2 py-1"
                  title={small ? `Fewer than ${threshold} completed — standard reads are suppressed; a consultant reveal is logged in the audit trail.` : undefined}
                  style={{
                    border: `1px solid ${small ? "#D9A441" : "#E8E8E8"}`,
                    background: small ? "#FBF3E2" : "white",
                    fontFamily: "Oswald", letterSpacing: "0.05em",
                  }}
                  data-testid={`subgroup-chip-${k}-${v ?? "unassigned"}`}
                >
                  {v ?? "Unassigned"} · {members.length} · {done} done{small ? ` · n<${threshold}` : ""}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
